/**
 * Purpose: Home screen — app entry point with branding, mode selection, and resume game.
 * Owns: App title/branding, game mode selection cards, continue game button,
 *       navigation to mode-specific setup screens.
 * Notes: This is the root experience. Selecting a mode navigates to that mode's
 *        dedicated setup route (/setup/race, /setup/honor-roll, /setup/classroom).
 *        "Continue Game" only appears if a saved game exists in localStorage.
 *        Premium chalkboard aesthetic with wood frame (CS-22: theme tokens only).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { ModeCard } from '../components/setup/ModeCard';
import { GAME_MODES } from '../config/modeConfig';
import { hasSavedGame, loadGame } from '../utils/saveGame';
import familyRoadTripPack from '../data/packs/base';
import type { GameMode } from '../types/game';

export function HomePage() {
  const navigate = useNavigate();
  const { resetToSetup, restoreGame, loadPack } = useGame();
  const [savedGameExists, setSavedGameExists] = useState(false);
  const [selectedMode, setSelectedMode] = useState<GameMode | null>(null);

  /* Check for saved game on mount */
  useEffect(() => {
    setSavedGameExists(hasSavedGame());
  }, []);

  /** Reset context state when starting a new game so setup pages start clean. */
  function handleModeSelect(modeId: GameMode) {
    setSelectedMode(modeId);
  }

  function handleNewGame() {
    if (!selectedMode) return;
    const mode = GAME_MODES.find((m) => m.id === selectedMode);
    if (!mode) return;

    /* Reset any existing game state before navigating to setup */
    resetToSetup();
    navigate(`/setup/${mode.routeSlug}`);
  }

  function handleContinueGame() {
    const savedState = loadGame();
    if (!savedState) {
      setSavedGameExists(false);
      return;
    }
    /* Restore saved game state into context and load the question pack */
    restoreGame(savedState);
    loadPack(familyRoadTripPack);
    navigate('/play');
  }

  return (
    <div className="chalkboard-bg" style={styles.page}>
      <div className="wood-frame" style={styles.frame}>
        <div style={styles.content}>
          {/* Branding */}
          <div style={styles.brandBlock}>
            <h1 className="chalk-text-bright" style={styles.title}>
              Class Act
            </h1>
            <p className="chalk-text-gold" style={styles.tagline}>
              The family trivia game
            </p>
          </div>

          {/* Continue Game — only when a save exists */}
          {savedGameExists && (
            <button
              className="btn-gold"
              style={styles.continueButton}
              onClick={handleContinueGame}
              aria-label="Continue saved game"
            >
              Continue Game
            </button>
          )}

          {/* Mode selection */}
          <div style={styles.modeSection}>
            <p style={styles.modeLabel}>Choose Your Mode</p>
            <div style={styles.modeList}>
              {GAME_MODES.map((mode) => (
                <ModeCard
                  key={mode.id}
                  modeId={mode.id}
                  label={mode.label}
                  description={mode.description}
                  selected={selectedMode === mode.id}
                  onSelect={handleModeSelect}
                />
              ))}
            </div>
          </div>

          {/* Start new game button — appears after selecting a mode */}
          {selectedMode && (
            <button
              className="btn-gold"
              style={styles.startButton}
              onClick={handleNewGame}
              aria-label="Start new game with selected mode"
            >
              New Game
            </button>
          )}

          {/* Footer */}
          <p style={styles.footer}>by Blacksite Labs</p>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '1.5rem',
    minHeight: '100vh',
    overflowY: 'auto',
  },
  frame: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2.5rem 1.5rem',
    width: '100%',
    maxWidth: '480px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.75rem',
    textAlign: 'center' as const,
    width: '100%',
  },
  brandBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  title: {
    fontSize: '3rem',
    fontWeight: '400',
    letterSpacing: '0.02em',
  },
  tagline: {
    fontSize: '1.1rem',
    fontWeight: '400',
    fontFamily: 'var(--font-family-handwriting)',
  },
  continueButton: {
    padding: '0.9rem 2rem',
    fontSize: '1.15rem',
    fontFamily: 'var(--font-family)',
    width: '100%',
    maxWidth: '320px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 0 rgba(0,0,0,0.2)',
  },
  modeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    width: '100%',
  },
  modeLabel: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  modeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  startButton: {
    padding: '0.9rem 2rem',
    fontSize: '1.2rem',
    fontFamily: 'var(--font-family)',
    width: '100%',
    maxWidth: '320px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 0 rgba(0,0,0,0.2)',
  },
  footer: {
    fontSize: '0.75rem',
    color: 'var(--muted)',
    opacity: 0.6,
    fontFamily: 'var(--font-family)',
    marginTop: '0.5rem',
  },
};
